import React from 'react';

import jpost from "../../../assets/partners/jpost.com.png";
import dalidatlv from "../../../assets/partners/dalidatlv.co.il.png";
import ziv from "../../../assets/partners/ziv.org.il.png";
import israelhayom from "../../../assets/partners/israelhayom.co.il.png";
import chipest from "../../../assets/partners/chipest.co.il.png";
import eilatisrael from "../../../assets/partners/eilatisrael.co.il.png";

type SiteTier = 'premium' | 'boost' | 'super';
type OrderStatus = 'live' | 'inProgress' | 'queued';

interface DemoRow {
    anchor: string;
    url: string;
    partnerDomain: string;
    partnerLogo: string;
    tier: SiteTier;
    orderedAt: string;
    completedAt: string;
    status: OrderStatus;
}

const TIER_LABELS: Record<SiteTier, string> = {
    premium: 'פרימיום',
    boost: 'בוסט',
    super: 'סופר ריגו',
};

const STATUS_LABELS: Record<OrderStatus, string> = {
    live: 'באוויר',
    inProgress: 'בתהליך ביצוע',
    queued: 'בתור לפרסום',
};

// Fictional marketing sample - the whole card is aria-hidden, see HomeHero.
const DEMO_ROWS: DemoRow[] = [
    {
        anchor: 'עורכי דין',
        url: 'example-client-01.co.il',
        partnerDomain: 'jpost.com',
        partnerLogo: jpost,
        tier: 'premium',
        orderedAt: '12.03.2026',
        completedAt: '19.03.2026',
        status: 'live',
    },
    {
        anchor: 'עיצוב מטבחים מודרני',
        url: 'example-client-02.com',
        partnerDomain: 'dalidatlv.co.il',
        partnerLogo: dalidatlv,
        tier: 'boost',
        orderedAt: '08.03.2026',
        completedAt: '—',
        status: 'inProgress',
    },
    {
        anchor: 'חתונה בגן אירועים',
        url: 'example-client-03.co.il',
        partnerDomain: 'ziv.org.il',
        partnerLogo: ziv,
        tier: 'super',
        orderedAt: '27.02.2026',
        completedAt: '04.03.2026',
        status: 'live',
    },
    {
        anchor: 'ייעוץ פיננסי לעצמאים',
        url: 'example-client-04.co.il',
        partnerDomain: 'israelhayom.co.il',
        partnerLogo: israelhayom,
        tier: 'premium',
        orderedAt: '21.02.2026',
        completedAt: '01.03.2026',
        status: 'live',
    },
    {
        anchor: 'קליניקת שיקום פיזיותרפיה',
        url: 'example-client-05.co.il',
        partnerDomain: 'chipest.co.il',
        partnerLogo: chipest,
        tier: 'boost',
        orderedAt: '14.02.2026',
        completedAt: '20.02.2026',
        status: 'live',
    },
    {
        anchor: 'מסעדת שף כשרה',
        url: 'example-client-06.co.il',
        partnerDomain: 'eilatisrael.co.il',
        partnerLogo: eilatisrael,
        tier: 'super',
        orderedAt: '05.02.2026',
        completedAt: '—',
        status: 'queued',
    },
];

// Decorative fake-browser card. Rendered aria-hidden by HomeHero - the real
// information (the 3 stats) lives in focusable siblings outside this card.
const HeroDemoPanel: React.FC = () => (
    <div className="lnk-hero__card">
        <div className="lnk-hero__browser-bar">
            <div className="lnk-hero__dots">
                <i className="lnk-hero__dot lnk-hero__dot--red"/>
                <i className="lnk-hero__dot lnk-hero__dot--amber"/>
                <i className="lnk-hero__dot lnk-hero__dot--green"/>
            </div>
            <div className="lnk-hero__url-pill">linkandgo.co.il</div>
        </div>

        <div className="lnk-hero__panel">
            <div className="lnk-hero__panel-head">
                <div className="lnk-hero__panel-title">
                    <span className="lnk-hero__panel-icon">
                        <svg viewBox="0 0 24 24" width="12" height="12" stroke="#fff" fill="none"
                             strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M9 6l-6 6 6 6M15 6l6 6-6 6"/>
                        </svg>
                    </span>
                    <span className="lnk-hero__panel-name">ההזמנות שלי</span>
                </div>
                <div className="lnk-hero__panel-chips">
                    <span className="lnk-hero__chip">
                        <b>6</b> <span>באוויר</span>
                    </span>
                    <span className="lnk-hero__chip">
                        <b>2</b> <span>בתהליך</span>
                    </span>
                </div>
            </div>

            <div className="lnk-hero__table-wrap">
                <table className="lnk-hero__table">
                    <thead>
                    <tr>
                        <th className="lnk-hero__th lnk-hero__th--start">אנקור</th>
                        <th className="lnk-hero__th">קישור לאנקור (URL שלך)</th>
                        <th className="lnk-hero__th">אתר מקשר</th>
                        <th className="lnk-hero__th lnk-hero__th--center">סוג אתר</th>
                        <th className="lnk-hero__th lnk-hero__th--center">תאריך הזמנה</th>
                        <th className="lnk-hero__th lnk-hero__th--center">תאריך ביצוע</th>
                        <th className="lnk-hero__th lnk-hero__th--center lnk-hero__th--end">סטטוס</th>
                    </tr>
                    </thead>
                    <tbody>
                    {DEMO_ROWS.map(row => (
                        <tr key={row.url}>
                            <td className="lnk-hero__td lnk-hero__td--anchor">{row.anchor}</td>
                            <td className="lnk-hero__td lnk-hero__td--url">
                                <bdi dir="ltr">{row.url}</bdi>
                            </td>
                            <td className="lnk-hero__td">
                                <span className="lnk-hero__partner">
                                    <span className="lnk-hero__logo-tile">
                                        <img src={row.partnerLogo} alt="" width={18} height={18}/>
                                    </span>
                                    <bdi dir="ltr" className="lnk-hero__partner-domain">{row.partnerDomain}</bdi>
                                </span>
                            </td>
                            <td className="lnk-hero__td lnk-hero__td--center">
                                <span className={`lnk-hero__pill lnk-hero__pill--${row.tier}`}>
                                    {TIER_LABELS[row.tier]}
                                </span>
                            </td>
                            <td className="lnk-hero__td lnk-hero__td--center lnk-hero__td--muted">{row.orderedAt}</td>
                            <td className="lnk-hero__td lnk-hero__td--center lnk-hero__td--muted">{row.completedAt}</td>
                            <td className="lnk-hero__td lnk-hero__td--center lnk-hero__td--end">
                                <span className={`lnk-hero__pill lnk-hero__pill--${row.status}`}>
                                    {STATUS_LABELS[row.status]}
                                </span>
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>
        </div>
    </div>
);

export default HeroDemoPanel;
